import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "motion/react";
import {
  Upload,
  Image as ImageIcon,
  Sparkles,
  Download,
  RefreshCw,
  Check,
  Info,
  History,
  Trash2,
  Maximize2,
  X,
  User,
  LogIn,
  ShoppingBag,
  Menu,
  DollarSign,
} from "lucide-react";
import confetti from "canvas-confetti";
import { cn } from "@/src/lib/utils";
import { COLOR_STYLES, ColorStyle, ColorizationResult } from "./types";
import { colorizeImage } from "./lib/ai";
import { CompareSlider } from "./components/CompareSlider";
import { useAuth } from "./contexts/AuthContext";
import { LoginForm } from "./components/auth/LoginForm";
import { RegisterForm } from "./components/auth/RegisterForm";
import { ProfileModal } from "./components/auth/ProfileModal";
import { PrintStore } from "./components/store/PrintStore";
import { PricingPage } from "./components/PricingPage";

// Sample B&W demo images
const DEMO_IMAGES = [
  {
    name: "Landscape",
    url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80&sat=-100",
    thumb:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=200&q=80&sat=-100",
  },
  {
    name: "Portrait",
    url: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=800&q=80&sat=-100",
    thumb:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&q=80&sat=-100",
  },
  {
    name: "Street",
    url: "https://images.unsplash.com/photo-1517732306149-e8f829eb588a?w=800&q=80&sat=-100",
    thumb:
      "https://images.unsplash.com/photo-1517732306149-e8f829eb588a?w=200&q=80&sat=-100",
  },
  {
    name: "Architecture",
    url: "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=800&q=80&sat=-100",
    thumb:
      "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=200&q=80&sat=-100",
  },
];

export default function App() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showPrintStore, setShowPrintStore] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState<"home" | "pricing">("home");
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>("image/jpeg");
  const [selectedStyle, setSelectedStyle] = useState<ColorStyle>(
    COLOR_STYLES[0],
  );
  const [variationCount, setVariationCount] = useState<number>(3);
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState<ColorizationResult[]>([]);
  const [selectedResult, setSelectedResult] =
    useState<ColorizationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load history from localStorage (only for authenticated users)
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setResults([]);
      return;
    }
    try {
      const saved = localStorage.getItem(`ritsu-history-${user.id}`);
      if (saved) {
        setResults(JSON.parse(saved));
      }
    } catch (e) {
      console.error("Failed to load history from localStorage:", e);
      localStorage.removeItem(`ritsu-history-${user.id}`);
    }
  }, [isAuthenticated, user]);

  // Save history to localStorage (only for authenticated users)
  useEffect(() => {
    if (!isAuthenticated || !user || results.length === 0) return;
    try {
      localStorage.setItem(`ritsu-history-${user.id}`, JSON.stringify(results));
    } catch (e) {
      console.error("Failed to save history to localStorage:", e);
      if (e instanceof Error && e.name === "QuotaExceededError") {
        const trimmed = results.slice(0, 5);
        try {
          localStorage.setItem(`ritsu-history-${user.id}`, JSON.stringify(trimmed));
        } catch (innerError) {
          console.error(
            "Still failing to save even after trimming",
            innerError,
          );
        }
      }
    }
  }, [results, isAuthenticated, user]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    const file = acceptedFiles[0];
    if (file) {
      setMimeType(file.type);
      const reader = new FileReader();
      reader.onload = () => {
        setOriginalImage(reader.result as string);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  }, [isAuthenticated]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  } as any);

  const handleGenerate = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    if (!originalImage) return;

    setIsGenerating(true);
    setError(null);
    const newResults: ColorizationResult[] = [];

    try {
      // Generate variations sequentially for better stability
      for (let i = 0; i < variationCount; i++) {
        try {
          const colorizedUrl = await colorizeImage(
            originalImage,
            mimeType,
            selectedStyle.prompt,
            i,
          );

          const result: ColorizationResult = {
            id: Math.random().toString(36).substring(7),
            imageUrl: colorizedUrl,
            styleId: selectedStyle.id,
            timestamp: Date.now(),
          };
          newResults.push(result);
        } catch (innerErr: any) {
          console.error(`Variation ${i} failed:`, innerErr);
          // Continue to next variation if one fails, unless it's the first one
          if (i === 0) throw innerErr;
        }
      }

      if (newResults.length > 0) {
        setResults((prev) => [...newResults, ...prev]);
        setSelectedResult(newResults[0]);
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ["#3b82f6", "#8b5cf6", "#ec4899"],
        });
      } else {
        throw new Error("Failed to generate any variations.");
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to transform your image. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadImage = (url: string, name: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = `ritsu-${name}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearHistory = () => {
    setResults([]);
    setSelectedResult(null);
    if (user) {
      localStorage.removeItem(`ritsu-history-${user.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-blue-500/30">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-black/50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">
              Ritsu
            </h1>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => setCurrentPage("home")}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                currentPage === "home"
                  ? "bg-white/10 text-white"
                  : "text-white/60 hover:text-white hover:bg-white/5",
              )}
            >
              Home
            </button>
            <button
              onClick={() => setCurrentPage("pricing")}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2",
                currentPage === "pricing"
                  ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                  : "text-white/60 hover:text-white hover:bg-white/5",
              )}
            >
              <DollarSign className="w-4 h-4" />
              Harga
            </button>
          </div>

          <div className="flex items-center gap-4">
            {currentPage === "home" && results.length > 0 && (
              <button
                onClick={clearHistory}
                className="p-2 text-white/40 hover:text-red-400 transition-colors"
                title="Clear History"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <div className="h-4 w-px bg-white/10 md:block hidden" />
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="p-2 md:hidden text-white/60 hover:text-white"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Auth Button */}
            {authLoading ? (
              <div className="w-8 h-8 rounded-full bg-white/5 animate-pulse" />
            ) : isAuthenticated ? (
              <button
                onClick={() => setShowProfile(true)}
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xs font-bold">
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.name || user.email}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    (user?.name || user?.email || "U").charAt(0).toUpperCase()
                  )}
                </div>
                <span className="text-sm text-white/80 hidden sm:block">
                  {user?.name || user?.email?.split("@")[0]}
                </span>
              </button>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors text-sm font-medium"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">Sign In</span>
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {showMobileMenu && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-white/5 bg-black/90 backdrop-blur-xl"
            >
              <div className="px-6 py-4 space-y-2">
                <button
                  onClick={() => {
                    setCurrentPage("home");
                    setShowMobileMenu(false);
                  }}
                  className={cn(
                    "w-full text-left px-4 py-3 rounded-lg transition-colors",
                    currentPage === "home"
                      ? "bg-white/10 text-white"
                      : "text-white/60",
                  )}
                >
                  Home
                </button>
                <button
                  onClick={() => {
                    setCurrentPage("pricing");
                    setShowMobileMenu(false);
                  }}
                  className={cn(
                    "w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-2",
                    currentPage === "pricing"
                      ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                      : "text-white/60",
                  )}
                >
                  <DollarSign className="w-4 h-4" />
                  Harga
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Mobile Auth */}
      {authLoading ? null : isAuthenticated ? (
        <button
          onClick={() => setShowProfile(true)}
          className="md:hidden fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30"
        >
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.name || user.email}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              (user?.name || user?.email || "U").charAt(0).toUpperCase()
            )}
          </div>
        </button>
      ) : (
        <button
          onClick={() => setShowAuthModal(true)}
          className="md:hidden fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center"
        >
          <LogIn className="w-5 h-5" />
        </button>
      )}

      {currentPage === "pricing" ? (
        <PricingPage />
      ) : (
      <main className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left Column: Controls */}
        <div className="lg:col-span-4 space-y-8">
          <section className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-white/60 uppercase tracking-wider">
              <Upload className="w-4 h-4" />
              Step 1: Upload Image
            </div>

            <div
              {...getRootProps()}
              className={cn(
                "relative aspect-square rounded-2xl border-2 border-dashed transition-all duration-300 flex flex-col items-center justify-center gap-4 cursor-pointer overflow-hidden",
                isDragActive
                  ? "border-blue-500 bg-blue-500/5"
                  : "border-white/10 hover:border-white/20 bg-white/[0.02]",
                originalImage ? "border-solid border-white/10" : "",
              )}
            >
              <input {...getInputProps()} />

              {originalImage ? (
                <>
                  <img
                    src={originalImage}
                    alt="Original"
                    className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale"
                    referrerPolicy="no-referrer"
                  />
                  <div className="relative z-10 bg-black/60 backdrop-blur-md p-4 rounded-xl border border-white/10 flex flex-col items-center gap-2">
                    <RefreshCw className="w-6 h-6 text-blue-400" />
                    <span className="text-xs font-medium">Change Image</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
                    <Upload className="w-8 h-8 text-white/40" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium">Drop your photo here</p>
                    <p className="text-sm text-white/40 mt-1">
                      B&W photos work best
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Demo Images */}
            {!originalImage && (
              <div className="mt-4">
                <p className="text-xs text-white/40 mb-3 text-center">
                  Or try a demo image:
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {DEMO_IMAGES.map((demo) => (
                    <button
                      key={demo.name}
                      onClick={() => {
                        if (!isAuthenticated) {
                          setShowAuthModal(true);
                          return;
                        }
                        setOriginalImage(demo.url);
                        setError(null);
                      }}
                      className="aspect-square rounded-xl overflow-hidden border border-white/10 hover:border-blue-500/50 transition-all group"
                    >
                      <img
                        src={demo.thumb}
                        alt={demo.name}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
                        referrerPolicy="no-referrer"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-white/60 uppercase tracking-wider">
              <Sparkles className="w-4 h-4" />
              Step 2: Choose Style
            </div>

            <div className="grid grid-cols-2 gap-3">
              {COLOR_STYLES.map((style) => (
                <button
                  key={style.id}
                  onClick={() => setSelectedStyle(style)}
                  className={cn(
                    "p-4 rounded-xl border transition-all text-left relative overflow-hidden group",
                    selectedStyle.id === style.id
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-white/5 bg-white/[0.02] hover:border-white/20",
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xl">{style.emoji}</span>
                    {selectedStyle.id === style.id && (
                      <Check className="w-4 h-4 text-blue-400" />
                    )}
                  </div>
                  <div className="font-semibold text-sm">{style.label}</div>
                  <div className="text-[10px] text-white/40 mt-1 line-clamp-1">
                    {style.description}
                  </div>

                  {/* Hover Accent */}
                  <div
                    className={cn(
                      "absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent transition-all duration-500",
                      selectedStyle.id === style.id
                        ? "w-full"
                        : "w-0 group-hover:w-full",
                    )}
                  />
                </button>
              ))}
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-white/60 uppercase tracking-wider">
                <History className="w-4 h-4" />
                Variations
              </div>
              <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
                {[1, 3, 6].map((count) => (
                  <button
                    key={count}
                    onClick={() => setVariationCount(count)}
                    className={cn(
                      "px-3 py-1 rounded-md text-xs font-bold transition-all",
                      variationCount === count
                        ? "bg-white text-black"
                        : "text-white/40 hover:text-white",
                    )}
                  >
                    {count}
                  </button>
                ))}
              </div>
            </div>

            <button
              disabled={!originalImage || isGenerating}
              onClick={handleGenerate}
              className={cn(
                "w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all relative overflow-hidden group",
                !originalImage || isGenerating
                  ? "bg-white/5 text-white/20 cursor-not-allowed"
                  : "bg-white text-black hover:scale-[1.02] active:scale-[0.98]",
              )}
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Bring to Life
                </>
              )}

              {/* Progress Bar (Simulated) */}
              {isGenerating && (
                <div className="absolute bottom-0 left-0 h-1 bg-blue-500 animate-[progress_2s_ease-in-out_infinite]" />
              )}
            </button>

            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-start gap-2">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                {error}
              </div>
            )}
          </section>
        </div>

        {/* Right Column: Preview & Results */}
        <div className="lg:col-span-8 space-y-8">
          {/* Main Preview */}
          <section className="relative aspect-[4/3] lg:aspect-video rounded-3xl bg-white/[0.02] border border-white/5 overflow-hidden flex items-center justify-center">
            <AnimatePresence mode="wait">
              {selectedResult ? (
                <motion.div
                  key={selectedResult.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  className="w-full h-full p-4"
                >
                  <CompareSlider
                    before={originalImage!}
                    after={selectedResult.imageUrl}
                    className="w-full h-full"
                  />

                  <div className="absolute top-8 right-8 flex gap-3">
                    <button
                      onClick={() => {
                        if (!isAuthenticated) {
                          setShowAuthModal(true);
                          return;
                        }
                        setShowPrintStore(true);
                      }}
                      className="px-4 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 backdrop-blur-md flex items-center gap-2 text-white font-medium hover:opacity-90 transition-all"
                      title="Order Prints"
                    >
                      <ShoppingBag className="w-4 h-4" />
                      <span className="hidden sm:inline">Order Print</span>
                    </button>
                    <button
                      onClick={() =>
                        downloadImage(
                          selectedResult.imageUrl,
                          selectedResult.styleId,
                        )
                      }
                      className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all"
                      title="Download"
                    >
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              ) : originalImage ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center gap-6"
                >
                  <img
                    src={originalImage}
                    alt="Preview"
                    className="max-h-[60vh] rounded-2xl shadow-2xl grayscale"
                    referrerPolicy="no-referrer"
                  />
                  <div className="text-center space-y-2">
                    <p className="text-lg font-medium">Ready to transform</p>
                    <p className="text-sm text-white/40">
                      Select a style and click generate
                    </p>
                  </div>
                </motion.div>
              ) : (
                <div className="flex flex-col items-center gap-4 text-white/20">
                  <ImageIcon className="w-20 h-20 stroke-[1]" />
                  <p className="text-lg font-medium">No image uploaded yet</p>
                </div>
              )}
            </AnimatePresence>
          </section>

          {/* Results Grid */}
          {results.length > 0 && (
            <section className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">Recent Generations</h3>
                <span className="text-xs font-medium text-white/40">
                  {results.length} items
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {results.map((result) => (
                  <motion.button
                    layout
                    key={result.id}
                    onClick={() => setSelectedResult(result)}
                    className={cn(
                      "relative aspect-square rounded-2xl overflow-hidden border-2 transition-all group",
                      selectedResult?.id === result.id
                        ? "border-blue-500"
                        : "border-transparent hover:border-white/20",
                    )}
                  >
                    <img
                      src={result.imageUrl}
                      alt="Result"
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Maximize2 className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute bottom-2 left-2 px-1.5 py-0.5 bg-black/60 backdrop-blur-md rounded text-[8px] font-bold uppercase tracking-tighter">
                      {COLOR_STYLES.find((s) => s.id === result.styleId)?.label}
                    </div>
                  </motion.button>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      )}

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 mt-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6 text-white/40 text-sm">
          <p>© 2026 Ritsu. All rights reserved.</p>
          <div className="flex items-center gap-8">
            <a href="#" className="hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Terms of Service
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Contact
            </a>
          </div>
        </div>
      </footer>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes progress {
          0% { width: 0; left: 0; }
          50% { width: 70%; left: 15%; }
          100% { width: 0; left: 100%; }
        }
      `,
        }}
      />

      {/* Auth Modal */}
      <AnimatePresence>
        {showAuthModal && !authLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
          >
            <button
              onClick={() => setShowAuthModal(false)}
              className="absolute top-6 right-6 p-2 text-white/40 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            {authMode === "login" ? (
              <LoginForm 
                onSwitchToRegister={() => setAuthMode("register")} 
                onSuccess={() => setShowAuthModal(false)}
              />
            ) : (
              <RegisterForm 
                onSwitchToLogin={() => setAuthMode("login")} 
                onSuccess={() => setShowAuthModal(false)}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Modal */}
      <ProfileModal isOpen={showProfile} onClose={() => setShowProfile(false)} />

      {/* Print Store Modal */}
      <AnimatePresence>
        {showPrintStore && selectedResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl overflow-y-auto"
          >
            <div className="max-w-6xl mx-auto p-6">
              <PrintStore
                selectedImage={selectedResult.imageUrl}
                onBack={() => setShowPrintStore(false)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
